import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import CountryCodeProvider from "./CountryCodeProvider";
import { CountryCodeList } from "../../model/country_code/CountryCodeModel";
import { useDispatch } from "react-redux";
import { clickedCountryCode } from "../../libraries/state_reducers/country_code/CountryCodeReducer";
import { removeModal } from "../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../const/ModalKeys";

type CountryCodeContextType = {
  countryCodeList: Array<CountryCodeList> | null;
  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  handleOnClickCode: (detail: CountryCodeList) => void;
  clearSearch: () => void;
};

const CountryCodeContext = createContext<CountryCodeContextType | undefined>(
  undefined
);

export const CountryCodeContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  const searchValueRef = useRef<string>("");
  const countryCodeListRef = useRef<Array<CountryCodeList> | []>([]);

  const [countryCodeList, setCountryCodeList] =
    useState<Array<CountryCodeList> | null>(null);

  const [searchValue, setSearchValue] = useState<string>("");

  const handleOnClickCode = (detail: CountryCodeList) => {
    dispatch(clickedCountryCode({ ...detail }));
    dispatch(removeModal(ModalKeys.country_code_selection));
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-country-code`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<CountryCodeList> | undefined =
        countryCodeListRef.current?.filter((country) =>
         Object.values(country)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setCountryCodeList(filtered!);
    }
  };

  const clearSearch = () => {
    setCountryCodeList(countryCodeListRef.current);
    setSearchValue("");
     searchValueRef.current = "";
  };

  const countryCodeInit = async () => {
    const countryCodeModel = await CountryCodeProvider.getCountryAPI();
    if (countryCodeModel !== null) {
      const countryCodeList: Array<CountryCodeList> | null =
        countryCodeModel?.result?.country_code_list!;

      setCountryCodeList(countryCodeList);
      countryCodeListRef.current = countryCodeList;
    }
  };

  useEffect(() => {
    countryCodeInit();
  }, []);

  return (
    <CountryCodeContext.Provider
      value={{
        countryCodeList,
        searchValue,
        handleOnChange,
        handleOnClickCode,
        clearSearch,
      }}
    >
      {children}
    </CountryCodeContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const CountryCodeContextImport = () => {
  const context = useContext(CountryCodeContext);
  if (!context) {
    throw new Error(
      "CountryCodeContextImport must be used within a CountryCodeContextProvider"
    );
  }
  return context;
};
